import { useState, useEffect } from 'react';
import { CheckCircle2, Trophy, Target, Clock } from 'lucide-react';
import axios from 'axios';
import { roundLabel } from '../utils/wcLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, value, label, accentColor }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md px-3 py-4 flex flex-col items-center justify-center min-w-0">
      <Icon className="w-5 h-5 mb-1.5" style={{ color: accentColor }} />
      <div className="text-2xl font-black text-[#1A1A1A]" data-testid="round-stat-value">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function SportColumn({ sport, data, currentUserId }) {
  const badgeClass = sport === 'AFL' ? 'tipster-badge-afl'
    : sport === 'NRL' ? 'tipster-badge-nrl'
    : sport === 'EPL' ? 'tipster-badge-epl'
    : sport === 'AL'  ? 'tipster-badge-al'
    : 'tipster-badge-wc';
  if (!data) return null;
  const { round_number, completed_matches, total_matches, my_stats, rankings } = data;
  const isOddsBased = sport === 'EPL' || sport === 'AL';
  // For odds-based sports show "X / Y points" (points earned / max possible
  // based on tips). Backend nulls out max_possible_points for OTHER users
  // until the reveal deadline — the logged-in user always sees their own.
  const oddsScore = isOddsBased && (my_stats.max_possible_points || 0) > 0
    ? `${(Math.round((my_stats.total_points || 0) * 100) / 100).toFixed(2)}/${(Math.round(my_stats.max_possible_points * 100) / 100).toFixed(2)}`
    : (isOddsBased ? (Math.round((my_stats.total_points || 0) * 100) / 100).toFixed(2) : null);

  return (
    <div className="space-y-4" data-testid={`round-stats-${sport}`}>
      {/* Sport header */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={badgeClass}>{sport}</span>
        <span className="text-base font-bold text-[#1A1A1A]">
          {roundLabel(sport, round_number)}
        </span>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-sm font-semibold">
          {completed_matches}/{total_matches} complete
        </span>
      </div>

      {/* Top stat cards: personal stats when logged in, aggregate when not */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard
          icon={CheckCircle2}
          value={my_stats.correct}
          label={my_stats.is_aggregate ? 'Top Correct' : 'Correct'}
          accentColor="#34C759"
        />
        <StatCard
          icon={Trophy}
          value={isOddsBased ? oddsScore : my_stats.bonus_pts}
          label={isOddsBased
            ? ((my_stats.max_possible_points || 0) > 0
                ? (my_stats.is_aggregate ? 'Top / Max' : 'Points / Max')
                : (my_stats.is_aggregate ? 'Top Score' : 'Points'))
            : (my_stats.is_aggregate ? 'Top Score' : 'Bonus Pts')}
          accentColor="#FFB81C"
        />
        <StatCard
          icon={Target}
          value={`${my_stats.accuracy}%`}
          label={my_stats.is_aggregate ? 'Avg Accuracy' : 'Accuracy'}
          accentColor="#FF9500"
        />
        <StatCard icon={Clock} value={completed_matches} label="Completed" accentColor="#8E8E93" />
      </div>

      {/* Rankings table */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {rankings.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">No tippers yet</div>
        ) : (
          rankings.map((r) => {
            const isMe = r.user_id === currentUserId;
            // Before the reveal deadline, other users' correct/accuracy are hidden.
            // Show a neutral "Submitted"/"No tips yet" label instead of a misleading "0/X correct".
            const preReveal = r.hidden === true;
            const summaryLabel = preReveal
              ? (r.tips_count > 0 ? 'Submitted ✓' : 'No tips yet')
              : `${r.correct}/${r.tips_count} correct`;
            return (
              <div
                key={r.user_id}
                className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 ${
                  isMe ? 'bg-yellow-50/50' : 'hover:bg-gray-50'
                }`}
                data-testid={`round-rank-${sport}-${r.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm font-bold text-gray-400 w-5">{r.rank}</span>
                  <div className="min-w-0">
                    <div className={`text-sm font-bold truncate ${isMe ? 'text-[#1A1A1A]' : 'text-[#1A1A1A]'}`}>
                      {r.name}
                      {isMe && <span className="text-[#007AFF] font-semibold ml-1.5">(You)</span>}
                    </div>
                    <div className="text-xs text-gray-500">{summaryLabel}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-black text-[#1A1A1A]" data-testid={`round-row-points-${sport}-${r.user_id}`}>
                    {isOddsBased && (r.max_possible_points || 0) > 0
                      ? `${(Math.round((r.total_points || 0) * 100) / 100).toFixed(2)}/${(Math.round(r.max_possible_points * 100) / 100).toFixed(2)} pts`
                      : (preReveal ? '—' : `${(Math.round((r.total_points || 0) * 100) / 100).toFixed(2)} pts`)}
                  </div>
                  <div className="text-xs text-gray-500">{preReveal ? '' : `${r.accuracy}%`}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

import CombinedLeaderboard from './CombinedLeaderboard';
import useCompetitionStatus from '../hooks/useCompetitionStatus';

export default function CurrentRoundStats({ currentUserId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const compStatus = useCompetitionStatus();
  const closedSet = new Set(compStatus?.closed_sports || []);
  // Order + which sports render a per-round card on the Dashboard. Any sport
  // the backend flags as closed is dropped, so once NRL/EPL end they'll come
  // off the home page automatically.
  const activeSports = ['AFL', 'NRL', 'EPL', 'AL', 'WC'].filter(s => !closedSet.has(s) && data?.[s]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/dashboard/round-stats`, { withCredentials: true })
      .then((res) => mounted && setData(res.data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If round-stats fetch failed, still render the leaderboard slot so the home
  // page always shows standings (the leaderboard has its own independent data
  // source via /api/leaderboard/combined/all).
  if (!data) {
    return (
      <section data-testid="current-round-stats">
        <div data-testid="round-stats-leaderboard-slot">
          <CombinedLeaderboard currentUserId={currentUserId} />
        </div>
      </section>
    );
  }

  return (
    <section data-testid="current-round-stats">
      <h2 className="text-2xl font-bold uppercase tracking-tight mb-6">Current Round Statistics</h2>
      <div className="grid md:grid-cols-2 gap-8">
        {activeSports.map(s => (
          <SportColumn key={s} sport={s} data={data[s]} currentUserId={currentUserId} />
        ))}
        <div data-testid="round-stats-leaderboard-slot" className="md:col-span-2">
          <CombinedLeaderboard currentUserId={currentUserId} />
        </div>
      </div>
    </section>
  );
}
